import { IFileToUpload } from "../../../../../interfaces/IFileToUpload";
import { IOfficeFileDto } from "../../../../../interfaces/IOfficeFileDto";
import { IOutlookRestDto } from "../../../../../interfaces/IOutlookRestDto";

export interface PIIRequest {
    accessToken: string;
    siteUrl?: string;
    fileUrl?: string;
    fileData?: string;
    forceRecheck?: boolean;
    redactPiiData?: boolean;
}

export interface PIIResult {
    piiDetected?: boolean;
    pii?: string;
    piiError?: string;
    isChecking?: boolean;
    rawPII?: string[];
}

export interface GraphItemPIIRequest {
    accessToken: string;
    driveId?: string;
    itemId?: string;
    fileName?: string;
    redactPiiData?: boolean;
}

export interface ApprovalRequest {
    hookId: number;
    hookTypeId: number;
    hookName: string;
    tenantGuid: string;
    accountId: string;
    requestor: string;
    approvers: string[];
    dueDate?: Date;
    details: string;
    itemLinks: ItemLink[];
}

export interface ItemLink {
    name: string;
    sharingUrl?: string;
    fullWebUrl?: string;
    uniqueId: string;
    siteUrl: string;
    listId: string;
    listName?: string;
    serverRelativeUrl?: string;
    itemId?: string;
}

export interface ApprovalExecutionResult {
    success: boolean;
    errorMessage: string;
    statusCode: number;
    failedFileNames: string[];
}

export interface SignRequest {
    hookId: number;
    hookName: string;
    tenantGuid: string;
    accessToken: string;
    requestor: string;
    recipients: SignRequestRecipient[];
    message: string;
    documents: ItemLink[];
}

export interface SignRequestRecipient {
    firstName: string;
    lastName: string;
    email: string;
}

export interface SignRequestResult {
    packageId: string;
    designerUrl: string;
}

export interface FilePreCommitRequest {
    accessToken: string;
    siteUrl: string;
    destinationUrl: string;
    fileName: string;
    autoFolderCreation: boolean;
    ignoreSameUserLock?: boolean;
}

export interface FolderPreCommitRequest {
    accessToken: string;
    siteUrl: string;
    destinationUrl: string;
    folderName: string;
}

export interface FilePreCommitResult {
    exists: boolean;
    checkedOut: boolean;
    checkedOutBy: string;
    locked: boolean;
    lockedBy: string;
}

export enum HookType {
    AllAutomation = -1,
    AllApproval = 0,
    Approval = 1,
    Sign = 2,
    BatchApproval = 3,
    Generic = 4,
    SharePointItemCreate = 5,
    SharePointItemMetadataEdit = 6,
    Exact = 99
}

export interface IHookRequest {
    TenantGUID: string,
    HookTypeId: HookType
}

export interface IHookResult {
    id: number;
    typeId: number;
    name: string;
    description: string;
    sites: string[];
    libraries: string[];
    dueDateDays?: number;
    isPeoplePickerRequired?: boolean;
    peoplePickerTitle?: string;
    peoplePickerItemLimit?: number;
    blacklistedSites: string[];
    blacklistedLibraries: string[];
}

export interface IFileSaveResult {
    success: boolean,
    message: string,
    succeededFiles: IFileSaveFileItem[],
    failedFileNames: string[],
    // True when the files were saved but the compose-mode "send on save" email could not be sent
    // (e.g. the Graph send failed). The save still succeeded; the user must send the message manually.
    sendOnSaveFailed?: boolean
}

export interface IFileSaveFileItem {
    name: string,
    uniqueId: string
}

export enum UpdateTriggerAction {
    Default,
    CheckIn,
    Publish,
    Rename,
    NewTemplate
}

export interface UpdateTriggerRequest {
    accessToken: string;
    tenantGuid: string;
    accountId: string;
    siteUrl: string;
    listId: string;
    teamId: string;
    channelId: string;
    itemUniqueId: string;
    triggeredByAction: UpdateTriggerAction;
}

interface BaseMetadataUpdateRequest {
    accessToken: string;
    tenantGuid: string;
    accountId: string;
    siteUrl: string;
    listId: string;
    teamId: string;
    channelId: string;
    isFolder: boolean;
    isListItem: boolean;
    listSettings: ListSettings;
    triggeredByAction: UpdateTriggerAction;
    metadata: MetadataDto[];
}

export interface MetadataUpdateRequest extends BaseMetadataUpdateRequest {
    listItemUniqueId: string;
    isPublishCheckIn?: boolean;
}

export interface BulkMetadataUpdateRequest extends BaseMetadataUpdateRequest {
    listItemUniqueIds: string[];
}

export interface ReplaceFileRequest {
    accessToken: string;
    accountId: string;
    siteUrl: string;
    listId: string;
    fileUniqueId: string;
    fileName?: string;
    checkinType: CheckinTypes;
    metadata: MetadataDto[];
    outlookRestInfo?: IOutlookRestDto;
    emailItemId?: string;
    attachmentFile?: IFileToUpload;
    officeFile?: IOfficeFileDto;
}

export interface CreateDocumentFromTemplateRequest {
    destAccessToken: string;
    templateAccessToken: string;
    tenantGuid: string;
    accountId: string;
    destSiteUrl: string;
    destListId: string;
    destFolderUniqueId: string;
    templateSiteUrl: string;
    templateUniqueId: string;
    teamId: string;
    channelId: string;
    fileName: string;
    checkinType: CheckinTypes;
    metadata: MetadataDto[];
    customPlaceholders?: CustomPlaceholderValue[];
}

export interface CustomPlaceholderValue {
    name: string;
    type: string;
    value: string;
}

export interface ScanTemplateCustomFieldsRequest {
    accessToken: string;
    tenantGuid: string;
    accountId: string;
    siteUrl: string;
    listId: string;
    templateUniqueIds?: string[];
}

export interface TemplateScanResult {
    fileName: string;
    uniqueId: string;
    status: "updated" | "skipped" | "error";
    fields?: { name: string; type: string; formats?: string[] }[];
    errors?: string[];
}

// ---------------------------------------------------------------- TemplateAi (AI placeholder suggestions)

export interface TemplateAiProposal {
    id: number;
    family: string;
    label: string;
    displayLabel: string;
    token: string;
    format?: string;
    customType?: string;
    /** The located span — still the full literal even when only part of it is replaced. */
    anchorText: string;
    /** The substring of anchorText that becomes the token ("___________" of
     *  "Name: ___________"); absent means the whole anchor is replaced. Echoed back on
     *  apply/render, where the server re-derives it — never trusted from here. */
    valueText?: string;
    contextBefore?: string;
    contextAfter?: string;
    occurrences: number;
    /** 1-based occurrence this proposal is pinned to, for anchors that cannot be made unique
     *  (a caption and its blank in different table cells). Absent means "the first, and the
     *  reviewer may widen it to all" — a pinned proposal cannot be widened. */
    occurrenceIndex?: number;
    replaceAll: boolean;
    location?: string;
    slideIndex?: number;
    inHeaderOrFooter: boolean;
    sampleValue?: string;
    confidence: string;
    defaultChecked: boolean;
}

export interface TemplateAiRejectedProposal {
    family?: string;
    label?: string;
    anchorText?: string;
    reason: string;
    nearestLabel?: string;
}

export interface TemplateAiPreviewPayload {
    kind: "html" | "sheets" | "slides" | "none";
    html?: string;
    sheets?: { name: string; html: string }[];
    slideCount?: number;
}

export interface SuggestTemplatePlaceholdersRequest {
    accessToken: string;
    tenantGuid: string;
    siteUrl: string;
    listId: string;
    templateUniqueId: string;
    instruction?: string;
}

export interface SuggestTemplatePlaceholdersResponse {
    proposals: TemplateAiProposal[];
    rejected: TemplateAiRejectedProposal[];
    preview?: TemplateAiPreviewPayload;
    fileVersion?: string;
    vocabularyNote?: string;
    truncated?: boolean;
    message?: string;
}

/** The template as it stands, no model call and no daily-cap slot. What the panel opens on. */
export interface PreviewTemplateRequest {
    accessToken: string;
    tenantGuid: string;
    siteUrl: string;
    listId: string;
    templateUniqueId: string;
}

export interface PreviewTemplateResponse {
    preview?: TemplateAiPreviewPayload;
    fileVersion?: string;
    message?: string;
}

export interface ApplyTemplatePlaceholderProposalsRequest {
    accessToken: string;
    tenantGuid: string;
    siteUrl: string;
    listId: string;
    templateUniqueId: string;
    fileVersion: string;
    // STATELESS contract: the full accepted proposals ride back and are re-validated
    // server-side; ids alone would require server state that deliberately does not exist.
    acceptedProposals: TemplateAiProposal[];
}

export interface ApplyTemplatePlaceholderProposalsResponse {
    appliedProposalIds: number[];
    skipped: TemplateAiRejectedProposal[];
    scanResult?: TemplateScanResult;
}

export interface RenderProposalSlideRequest {
    accessToken: string;
    tenantGuid: string;
    siteUrl: string;
    listId: string;
    templateUniqueId: string;
    fileVersion: string;
    slideIndex: number;
    acceptedProposals: TemplateAiProposal[];
}

export interface RenderProposalSlideResponse {
    imageBase64: string;
    slideWidth: number;
    slideHeight: number;
    bounds: { proposalId: number; x: number; y: number; width: number; height: number }[];
}

export interface ConvertAndSaveAsPDFRequest {
    graphToken: string;
    sharePointToken: string;
    tenantGuid: string;
    accountId: string;
    driveId: string;
    itemId: string;
    siteUrl: string;
    listId: string;
    teamId?: string;
    channelId?: string;
    fileUniqueId: string;
    EnsureUniqueFilename: boolean;
    checkinType?: CheckinTypes;
}

export interface MetadataDto {
    type: string;
    internalName: string;
    displayName?: string;
    value: any;
}

export interface ListSettings {
    enableMinorVersions: boolean;
}

export interface ISharePointToEmailMapping {
    columnName: string;
    emailProperty: string;
}

export interface ExchangeMailFolderUploadRequest {
    sharePointToken: string;
    mailToken: string;
    siteUrl: string;
    listId: string;
    destinationUrl: string;
    autoFolderCreation: boolean;
    exchangeAccountEmail: string;
    mailItemCategory: string;
    mailFolderId: string;
    metadata: MetadataDto[];
    sharePointEmailMappings?: ISharePointToEmailMapping[];
    folderName?: string
}

export interface BackgroundTaskProgress {
    taskId: string;
    number: string;
    message: string;
    logs: string[];
}

export enum CheckinTypes {
    minorCheckIn = 0,
    majorCheckIn = 1,
    overwriteCheckIn = 2
}